// The bell in the header, the list it opens, and the banner + chime that announce a new notification while
// the CRM is open (browser push covers the closed-tab case: see push-sw.js). Live through the realtime
// event `notification.new`, which the server sends to the member's own room only after the change commits.
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { cn } from '@tatra/ui';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { errorMessage } from '../../lib/api.js';
import { useRealtime } from '../../lib/realtime.js';
import {
  type AppNotification,
  NOTIFICATIONS_KEY,
  prependNotification,
  timeAgo,
  useMarkAllRead,
  useMarkRead,
  useNotifications,
} from './api.js';
import { disablePush, enablePush, pushState, type PushState } from './push.js';
import { playChime, setSoundEnabled, soundEnabled } from './sound.js';

const BANNER_MS = 10_000;

export function NotificationCentre() {
  const qc = useQueryClient();
  const router = useRouter();
  const list = useNotifications();
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const [open, setOpen] = useState(false);
  const [banners, setBanners] = useState<AppNotification[]>([]);

  useRealtime<AppNotification>('notification.new', (n) => {
    prependNotification(qc, n);
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    setBanners((current) => [n, ...current.filter((b) => b.id !== n.id)].slice(0, 3));
    if (soundEnabled()) playChime();
  });

  const dismiss = useCallback((id: string) => setBanners((c) => c.filter((b) => b.id !== id)), []);
  const openNotification = useCallback(
    (n: AppNotification) => {
      if (!n.readAt) markRead.mutate(n.id);
      dismiss(n.id);
      setOpen(false);
      if (n.url) router.history.push(n.url);
    },
    [markRead, dismiss, router],
  );

  const unread = list.data?.unreadCount ?? 0;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="notification-bell"
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="notification-count"
            className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] leading-none font-semibold text-primary-fg"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <Panel
            items={list.data?.items}
            loading={list.isLoading}
            failed={list.isError}
            unread={unread}
            onClose={() => setOpen(false)}
            onOpenItem={openNotification}
            onMarkAll={() => markAll.mutate()}
          />,
          document.body,
        )}

      {createPortal(<Banners items={banners} onOpen={openNotification} onDismiss={dismiss} />, document.body)}
    </>
  );
}

function Panel({
  items,
  loading,
  failed,
  unread,
  onClose,
  onOpenItem,
  onMarkAll,
}: {
  items: AppNotification[] | undefined;
  loading: boolean;
  failed: boolean;
  unread: number;
  onClose: () => void;
  onOpenItem: (n: AppNotification) => void;
  onMarkAll: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-40" onClick={onClose} />
      <div
        role="dialog"
        aria-label="Notifications"
        data-testid="notification-panel"
        className={cn(
          'fixed inset-x-0 top-16 bottom-0 z-50 flex flex-col overflow-hidden border-t border-border bg-surface shadow-pop',
          'sm:inset-x-auto sm:top-[68px] sm:right-6 sm:bottom-auto sm:max-h-[min(36rem,calc(100dvh-5rem))] sm:w-[26rem] sm:rounded-xl sm:border',
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <h2 className="font-display text-base font-semibold text-text">Notifications</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMarkAll}
              disabled={unread === 0}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-primary hover:bg-primary-soft disabled:cursor-default disabled:text-text-faint disabled:hover:bg-transparent"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              Mark all read
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close notifications"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-text-muted hover:bg-surface-hover sm:hidden"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-text-muted">Loading…</p>
          ) : failed ? (
            <p className="px-4 py-8 text-center text-sm text-text-muted">Could not load notifications.</p>
          ) : !items?.length ? (
            <div data-testid="notification-empty" className="px-4 py-10 text-center">
              <Bell className="mx-auto mb-2 h-6 w-6 text-text-faint" aria-hidden="true" />
              <p className="text-sm font-medium text-text">You are all caught up</p>
              <p className="mt-0.5 text-sm text-text-muted">New leads and hand-overs show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onOpenItem(n)}
                    data-testid="notification-item"
                    data-unread={n.readAt ? 'false' : 'true'}
                    className="flex min-h-14 w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-hover"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        n.readAt ? 'bg-transparent' : 'bg-primary',
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block text-sm break-words text-text',
                          n.readAt ? 'font-normal' : 'font-semibold',
                        )}
                      >
                        {n.title}
                      </span>
                      {n.body && (
                        <span className="mt-0.5 line-clamp-2 block text-sm break-words text-text-muted">
                          {n.body}
                        </span>
                      )}
                      <span className="mt-1 block text-xs text-text-faint">{timeAgo(n.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DeviceSettings />
      </div>
    </>
  );
}

/** Sound and push, per browser: what this device does when something new arrives. */
function DeviceSettings() {
  const [sound, setSound] = useState(soundEnabled);
  const [push, setPush] = useState<PushState | 'checking'>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void pushState().then((s) => live && setPush(s));
    return () => {
      live = false;
    };
  }, []);

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    setSoundEnabled(next);
    if (next) playChime(); // also the tap that lets the browser play sound later
  };

  const run = async (action: () => Promise<PushState>) => {
    setBusy(true);
    setError(null);
    try {
      setPush(await action());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-border bg-surface-hover/40 px-4 py-3 text-sm">
      <div className="flex min-h-10 items-center justify-between gap-3">
        <span id="notification-sound-label" className="text-text">
          Sound
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={sound}
          aria-labelledby="notification-sound-label"
          data-testid="notification-sound"
          onClick={toggleSound}
          className={cn(
            'relative h-7 w-12 shrink-0 rounded-full transition-colors',
            sound ? 'bg-primary' : 'bg-border-strong',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform',
              sound && 'translate-x-5',
            )}
          />
          <span className="sr-only">{sound ? 'Sound on' : 'Sound off'}</span>
        </button>
      </div>

      <div>
        {push === 'on' ? (
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 text-text" data-testid="push-state">
              <Bell className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              Notifications are on for this device
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(disablePush)}
              className="inline-flex min-h-10 shrink-0 items-center rounded-lg px-2.5 font-medium text-text-muted hover:bg-surface-hover"
            >
              Turn off
            </button>
          </div>
        ) : push === 'blocked' ? (
          <p className="flex items-start gap-2 text-text-muted" data-testid="push-state">
            <BellOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Blocked in this browser. Allow notifications for this site in the browser settings, then come
            back.
          </p>
        ) : push === 'unsupported' ? (
          <p className="flex items-start gap-2 text-text-muted" data-testid="push-state">
            <BellOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            This browser cannot show notifications when the CRM is closed.
          </p>
        ) : (
          <button
            type="button"
            disabled={busy || push === 'checking'}
            onClick={() => void run(enablePush)}
            data-testid="push-enable"
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
            Enable notifications on this device
          </button>
        )}
        {error && (
          <p role="alert" className="mt-2 text-danger">
            {error}
          </p>
        )}
        <p className="mt-2 text-xs text-text-faint">On iPhone, add the CRM to the Home Screen first.</p>
      </div>
    </div>
  );
}

function Banners({
  items,
  onOpen,
  onDismiss,
}: {
  items: AppNotification[];
  onOpen: (n: AppNotification) => void;
  onDismiss: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-2 z-[60] flex flex-col items-center gap-2 px-3"
    >
      {items.map((n) => (
        <Banner key={n.id} n={n} onOpen={onOpen} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function Banner({
  n,
  onOpen,
  onDismiss,
}: {
  n: AppNotification;
  onOpen: (n: AppNotification) => void;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(n.id), BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [n.id, onDismiss]);
  return (
    <div
      role="status"
      data-testid="notification-banner"
      className="tatra-pop pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-primary/30 bg-surface p-3 shadow-pop"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
        <Bell className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold break-words text-text">{n.title}</span>
        {n.body && <span className="line-clamp-2 block text-sm break-words text-text-muted">{n.body}</span>}
      </span>
      <button
        type="button"
        onClick={() => onOpen(n)}
        className="inline-flex min-h-11 shrink-0 items-center rounded-lg bg-primary px-3.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
      >
        Open
      </button>
      <button
        type="button"
        onClick={() => onDismiss(n.id)}
        aria-label="Dismiss"
        className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-hover"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
